import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Loader, FileText, Phone, Mail, Search, Trash2, Edit2, CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';

const inputCls  = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";
const selectCls = `${inputCls} bg-white`;

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const BLANK_CLIENT = {
  business_name:'', contact_name:'', phone:'', email:'',
  abn:'', address:'', billing_cycle:'weekly',
  payment_method:'bank_transfer', credit_limit:'3000', notes:'',
};

const BLANK_JOB = {
  date:'', description:'', vehicle:'', pickup_suburb:'', dropoff_suburb:'', charge:'',
};

export default function B2B() {
  const { settings } = useSettings();
  const [clients, setClients]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch]         = useState('');
  const [sortBy, setSortBy]         = useState('outstanding');
  const [showAdd, setShowAdd]       = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState(null);
  const [toast, setToast]           = useState('');
  const [form, setForm]             = useState(BLANK_CLIENT);
  const [jobForm, setJobForm]       = useState(BLANK_JOB);
  const [saving, setSaving]         = useState(false);
  const [showInvoiceSelect, setShowInvoiceSelect] = useState(false);
  const [selectedJobIds, setSelectedJobIds]       = useState([]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/b2b/clients');
      setClients(r.data);
    } catch(e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const sj = k => e => setJobForm(f => ({ ...f, [k]: e.target.value }));

  // Filtered + sorted client list
  const filteredClients = clients
    .filter(c => !search || [c.business_name, c.contact_name, c.phone, c.email, c.address]
      .some(v => (v||'').toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => {
      if (sortBy === 'outstanding') return (b.outstanding||0) - (a.outstanding||0);
      if (sortBy === 'name')        return a.business_name.localeCompare(b.business_name);
      if (sortBy === 'jobs')        return (b.jobs||[]).length - (a.jobs||[]).length;
      return 0;
    });

  // Selected client (always fresh from clients state)
  const sel = selectedId ? clients.find(c => c.id === selectedId) : null;

  const addClient = async () => {
    if (!form.business_name || !form.contact_name || !form.phone) return;
    setSaving(true);
    try {
      const res = await api.post('/api/b2b/clients', { ...form, credit_limit: Number(form.credit_limit)||3000 });
      setShowAdd(false);
      setForm(BLANK_CLIENT);
      showToast('✅ Client added!');
      await load();
      setSelectedId(res.data.id);
    } catch(e) { alert('Failed to add client.'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await api.put(`/api/b2b/clients/${sel.id}`, { ...form, credit_limit: Number(form.credit_limit)||3000 });
      setShowEdit(false);
      showToast('✅ Client updated!');
      await load();
    } catch(e) { alert('Failed to update client.'); }
    finally { setSaving(false); }
  };

  const openEdit = () => {
    if (!sel) return;
    setForm({
      business_name: sel.business_name || '',
      contact_name:  sel.contact_name  || '',
      phone:         sel.phone         || '',
      email:         sel.email         || '',
      abn:           sel.abn           || '',
      address:       sel.address       || '',
      billing_cycle: sel.billing_cycle || 'weekly',
      payment_method: sel.payment_method || 'bank_transfer',
      credit_limit:  String(sel.credit_limit || 3000),
      notes:         sel.notes || '',
    });
    setShowEdit(true);
  };

  const addJob = async () => {
    if (!jobForm.date || !jobForm.description || !jobForm.charge || !sel) return;
    setSaving(true);
    try {
      await api.post('/api/b2b/jobs', {
        ...jobForm,
        client_id: sel.id,
        charge: Number(jobForm.charge),
      });
      setShowAddJob(false);
      setJobForm(BLANK_JOB);
      showToast('✅ Job added!');
      await load(); // load() updates clients, sel auto-updates via selectedId
    } catch(e) { alert('Failed to add job.'); }
    finally { setSaving(false); }
  };

  const togglePaid = async (jobId) => {
    await api.put(`/api/b2b/jobs/${jobId}/toggle-paid`);
    await load();
  };

  const toggleStatus = async () => {
    if (!sel) return;
    await api.put(`/api/b2b/clients/${sel.id}`, { status: sel.status==='active' ? 'inactive' : 'active' });
    await load();
    showToast(sel.status==='active' ? 'Client deactivated' : '✅ Client activated');
  };

  const openInvoiceSelect = () => {
    if (!sel) return;
    const unpaid = (sel.jobs||[]).filter(j => !j.paid);
    if (!unpaid.length) { alert('No unpaid jobs to invoice.'); return; }
    setSelectedJobIds(unpaid.map(j => j.id)); // pre-select all unpaid
    setShowInvoiceSelect(true);
  };

  const toggleJobSelect = (jobId) => {
    setSelectedJobIds(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  };

  const generateInvoice = async () => {
    if (!sel || !selectedJobIds.length) return;
    const jobsToInvoice = (sel.jobs||[]).filter(j => selectedJobIds.includes(j.id));
    const total = jobsToInvoice.reduce((s, j) => s + j.charge, 0);
    const due   = new Date(); due.setDate(due.getDate() + 14);
    setSaving(true);
    try {
      await api.post('/api/invoices', {
        client_id:      sel.id,
        client_name:    sel.business_name,
        client_email:   sel.email,
        invoice_type:   'b2b',
        items:          jobsToInvoice.map(j => ({
          desc:  `${j.date} — ${j.description}${j.vehicle ? ' ('+j.vehicle+')' : ''}${j.pickup_suburb ? ' · '+j.pickup_suburb : ''}`,
          rate:  j.charge,
          total: j.charge,
        })),
        amount:         total,
        payment_method: sel.payment_method,
        due_date:       due.toISOString().split('T')[0],
        notes:          `${sel.billing_cycle?.charAt(0).toUpperCase()+sel.billing_cycle?.slice(1)} invoice — ${jobsToInvoice.length} job${jobsToInvoice.length>1?'s':''}`,
      });
      showToast(`✅ Invoice for $${total.toLocaleString()} generated!`);
      setShowInvoiceSelect(false);
      setSelectedJobIds([]);
      await load();
    } catch(e) { alert('Failed to generate invoice.'); }
    finally { setSaving(false); }
  };

  // Summary stats for selected client
  const totalBilled  = (sel?.jobs||[]).reduce((s, j) => s + j.charge, 0);
  const totalPaid    = (sel?.jobs||[]).filter(j => j.paid).reduce((s, j) => s + j.charge, 0);
  const outstanding  = sel?.outstanding || 0;
  const creditLimit  = sel?.credit_limit || 3000;
  const creditUsedPct = Math.min((outstanding / creditLimit) * 100, 100);
  const overLimit    = outstanding > creditLimit;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">B2B Clients</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {clients.length} client{clients.length!==1?'s':''} ·
            <span className="text-amber-600 font-bold ml-1">
              ${clients.reduce((s,c) => s+(c.outstanding||0), 0).toLocaleString()} total outstanding
            </span>
          </p>
        </div>
        <button onClick={() => { setForm(BLANK_CLIENT); setShowAdd(true); }}
          className="flex items-center gap-2 bg-apex-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-600 shadow-md">
          <Plus className="w-4 h-4" /> Add Client
        </button>
      </div>

      <div className={`grid gap-4 ${sel ? 'md:grid-cols-[340px_1fr]' : 'grid-cols-1'}`}>

        {/* ── Client List ── */}
        <div className="space-y-3">
          {/* Search + Sort */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
              <Search className="w-4 h-4 text-gray-300 shrink-0" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search clients..." className="outline-none text-sm flex-1" />
              {search && <button onClick={() => setSearch('')} className="text-gray-300 text-xs hover:text-gray-500">✕</button>}
            </div>
            <div className="flex gap-1">
              {[['outstanding','By Outstanding'],['name','By Name'],['jobs','By Jobs']].map(([v,l]) => (
                <button key={v} onClick={() => setSortBy(v)}
                  className={`flex-1 py-1.5 rounded-xl text-xs font-bold border transition-all
                    ${sortBy===v ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-10 text-gray-400 bg-white rounded-2xl border border-gray-200">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="font-medium">{search ? 'No clients match your search' : 'No B2B clients yet'}</p>
              {!search && <p className="text-xs mt-1">Add your first business client above</p>}
            </div>
          ) : (
            filteredClients.map(c => {
              const isOver = (c.outstanding||0) > (c.credit_limit||3000);
              return (
                <div key={c.id} onClick={() => setSelectedId(c.id)}
                  className={`bg-white rounded-2xl border-2 p-4 cursor-pointer transition-all hover:shadow-md
                    ${selectedId===c.id ? 'border-apex-blue-500 shadow-md' : isOver ? 'border-red-200' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="font-bold text-gray-900 truncate">{c.business_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{c.contact_name} · {c.phone}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-xl font-black ${(c.outstanding||0) > 0 ? isOver ? 'text-red-600' : 'text-amber-600' : 'text-green-600'}`}>
                        ${(c.outstanding||0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-gray-400">outstanding</p>
                    </div>
                  </div>

                  {/* Credit bar */}
                  {(c.outstanding || 0) > 0 && (
                    <div className="mb-2">
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all"
                          style={{ width:`${Math.min(((c.outstanding||0)/(c.credit_limit||3000))*100,100)}%`,
                          backgroundColor: isOver ? '#ef4444' : '#f59e0b' }} />
                      </div>
                      {isOver && <p className="text-[10px] text-red-500 font-bold mt-0.5">⚠ Over credit limit</p>}
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.status==='active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.status}
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{c.billing_cycle}</span>
                    <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{(c.jobs||[]).length} jobs</span>
                    <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">${(c.total_paid||0).toLocaleString()} paid</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Client Detail ── */}
        {sel && (
          <div className="space-y-4">
            {/* Client Header Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              {/* Over-limit warning */}
              {overLimit && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-red-700">Over Credit Limit!</p>
                    <p className="text-xs text-red-600">${outstanding.toLocaleString()} outstanding of ${creditLimit.toLocaleString()} limit. Consider invoicing now.</p>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-black text-apex-blue-700">{sel.business_name}</h2>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {sel.phone && <a href={`tel:${sel.phone}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-apex-blue-700"><Phone className="w-3.5 h-3.5" />{sel.phone}</a>}
                    {sel.email && <a href={`mailto:${sel.email}`} className="flex items-center gap-1 text-xs text-gray-500 hover:text-apex-blue-700"><Mail className="w-3.5 h-3.5" />{sel.email}</a>}
                  </div>
                  {sel.abn     && <p className="text-xs text-gray-400 mt-0.5">ABN: {sel.abn}</p>}
                  {sel.address && <p className="text-xs text-gray-400">{sel.address}</p>}
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-black ${outstanding>0 ? overLimit?'text-red-600':'text-amber-600' : 'text-green-600'}`}>
                    ${outstanding.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">outstanding</p>
                  <p className="text-sm font-bold text-green-600 mt-0.5">${totalPaid.toLocaleString()} paid</p>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  ['Total Billed', `$${totalBilled.toLocaleString()}`, '#0D2B6B'],
                  ['Total Paid',   `$${totalPaid.toLocaleString()}`,   '#059669'],
                  ['Outstanding',  `$${outstanding.toLocaleString()}`, outstanding > 0 ? (overLimit ? '#ef4444' : '#f59e0b') : '#059669'],
                  ['Jobs',         (sel.jobs||[]).length,               '#7c3aed'],
                ].map(([l, v, c]) => (
                  <div key={l} className="text-center bg-gray-50 rounded-xl py-3 px-2">
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wide mb-1">{l}</p>
                    <p className="font-black text-base" style={{ color: c }}>{v}</p>
                  </div>
                ))}
              </div>

              {/* Credit limit bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Credit Used: ${outstanding.toLocaleString()} / ${creditLimit.toLocaleString()}</span>
                  <span className={`font-bold ${overLimit ? 'text-red-600' : 'text-gray-500'}`}>{Math.round(creditUsedPct)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width:`${creditUsedPct}%`, backgroundColor: overLimit ? '#ef4444' : creditUsedPct > 80 ? '#f59e0b' : '#059669' }} />
                </div>
              </div>

              {/* Tags */}
              <div className="flex gap-2 flex-wrap mb-4">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${sel.status==='active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{sel.status}</span>
                <span className="text-xs bg-blue-100 text-blue-700 font-bold px-3 py-1 rounded-full">{sel.billing_cycle}</span>
                <span className="text-xs bg-purple-100 text-purple-700 font-bold px-3 py-1 rounded-full capitalize">{sel.payment_method?.replace('_',' ')}</span>
                <span className="text-xs bg-gray-100 text-gray-600 font-bold px-3 py-1 rounded-full">Credit: ${creditLimit.toLocaleString()}</span>
              </div>

              {sel.notes && (
                <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4">
                  📝 {sel.notes}
                </p>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { setJobForm(BLANK_JOB); setShowAddJob(true); }}
                  className="flex items-center gap-1.5 bg-apex-blue-700 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-apex-blue-600">
                  <Plus className="w-4 h-4" /> Add Job
                </button>
                <button onClick={openInvoiceSelect}
                  className="flex items-center gap-1.5 bg-apex-yellow-500 text-apex-blue-700 px-4 py-2 rounded-xl text-sm font-bold hover:bg-apex-yellow-400">
                  <FileText className="w-4 h-4" /> Generate Invoice ({(sel.jobs||[]).filter(j=>!j.paid).length} unpaid)
                </button>
                <button onClick={openEdit}
                  className="flex items-center gap-1.5 border-2 border-gray-200 text-gray-600 px-4 py-2 rounded-xl text-sm font-bold hover:bg-gray-50">
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button onClick={toggleStatus}
                  className={`px-4 py-2 rounded-xl text-sm font-bold border-2 ${sel.status==='active'
                    ? 'border-red-200 text-red-500 hover:bg-red-50'
                    : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                  {sel.status==='active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>

            {/* Job Log */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-gray-900">Job Log</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{(sel.jobs||[]).length} total jobs</p>
                </div>
                {outstanding > 0 && (
                  <div className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full">
                    ${outstanding.toLocaleString()} outstanding
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {['Date','Description','Vehicle','Area','Charge','Status',''].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(sel.jobs||[]).length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-gray-400">
                        <p className="font-medium">No jobs logged yet</p>
                        <p className="text-xs mt-1">Click "Add Job" to log work for this client</p>
                      </td></tr>
                    )}
                    {(sel.jobs||[]).map(j => (
                      <tr key={j.id} className={`hover:bg-gray-50 transition-colors ${j.paid ? '' : 'bg-amber-50/30'}`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{j.date}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{j.description}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{j.vehicle || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{j.pickup_suburb || '—'}</td>
                        <td className="px-4 py-3 font-black text-apex-blue-700 text-base whitespace-nowrap">${j.charge?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => togglePaid(j.id)}
                            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all
                              ${j.paid
                                ? 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                                : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'}`}>
                            {j.paid ? '✓ Paid' : 'Unpaid'}
                          </button>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <button onClick={() => setDeleteJobId(j.id)}
                            className="p-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(sel.jobs||[]).length > 0 && (
                      <tr className="bg-apex-blue-700">
                        <td colSpan={4} className="px-4 py-3 font-bold text-white">Total Billed</td>
                        <td className="px-4 py-3 font-black text-apex-yellow-500 text-lg">${totalBilled.toLocaleString()}</td>
                        <td colSpan={2} className="px-4 py-3 text-white/60 text-xs">
                          ${totalPaid.toLocaleString()} paid · ${outstanding.toLocaleString()} outstanding
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Add Client Modal ── */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowAdd(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="font-black text-apex-blue-700">Add B2B Client</h2>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3">
              <Field label="Business Name" required><input value={form.business_name} onChange={sf('business_name')} className={inputCls} placeholder="ABC Auto Wreckers" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Name" required><input value={form.contact_name} onChange={sf('contact_name')} className={inputCls} placeholder="John Smith" /></Field>
                <Field label="Phone" required><input value={form.phone} onChange={sf('phone')} className={inputCls} placeholder="07 0000 0000" /></Field>
                <Field label="Email"><input value={form.email} onChange={sf('email')} className={inputCls} placeholder="john@abc.com.au" /></Field>
                <Field label="ABN"><input value={form.abn} onChange={sf('abn')} className={inputCls} placeholder="XX XXX XXX XXX" /></Field>
              </div>
              <Field label="Address"><input value={form.address} onChange={sf('address')} className={inputCls} placeholder="123 Main St, Brisbane QLD" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Billing Cycle">
                  <select value={form.billing_cycle} onChange={sf('billing_cycle')} className={selectCls}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </Field>
                <Field label="Payment Method">
                  <select value={form.payment_method} onChange={sf('payment_method')} className={selectCls}>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="payid">PayID</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                </Field>
                <Field label="Credit Limit ($)"><input type="number" value={form.credit_limit} onChange={sf('credit_limit')} className={inputCls} /></Field>
              </div>
              <Field label="Notes"><textarea value={form.notes} onChange={sf('notes')} className={`${inputCls} resize-none`} rows={2} placeholder="Any special notes about this client..." /></Field>
              <button onClick={addClient} disabled={!form.business_name || !form.contact_name || !form.phone || saving}
                className="w-full bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Client Modal ── */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowEdit(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="font-black text-apex-blue-700">Edit — {sel?.business_name}</h2>
              <button onClick={() => setShowEdit(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3">
              <Field label="Business Name" required><input value={form.business_name} onChange={sf('business_name')} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Name" required><input value={form.contact_name} onChange={sf('contact_name')} className={inputCls} /></Field>
                <Field label="Phone" required><input value={form.phone} onChange={sf('phone')} className={inputCls} /></Field>
                <Field label="Email"><input value={form.email} onChange={sf('email')} className={inputCls} /></Field>
                <Field label="ABN"><input value={form.abn} onChange={sf('abn')} className={inputCls} /></Field>
              </div>
              <Field label="Address"><input value={form.address} onChange={sf('address')} className={inputCls} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Billing Cycle">
                  <select value={form.billing_cycle} onChange={sf('billing_cycle')} className={selectCls}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </Field>
                <Field label="Payment Method">
                  <select value={form.payment_method} onChange={sf('payment_method')} className={selectCls}>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="payid">PayID</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                </Field>
                <Field label="Credit Limit ($)"><input type="number" value={form.credit_limit} onChange={sf('credit_limit')} className={inputCls} /></Field>
              </div>
              <Field label="Notes"><textarea value={form.notes} onChange={sf('notes')} className={`${inputCls} resize-none`} rows={2} /></Field>
              <button onClick={saveEdit} disabled={saving}
                className="w-full bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Job Modal ── */}
      {showAddJob && sel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowAddJob(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-apex-blue-700 rounded-t-2xl px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Log a Job</p>
                  <p className="text-white font-black text-lg mt-0.5">{sel.business_name}</p>
                </div>
                <button onClick={() => setShowAddJob(false)} className="bg-white/15 text-white p-2 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date" required><input type="date" value={jobForm.date} onChange={sj('date')} className={inputCls} /></Field>
                <Field label="Charge ($)" required><input type="number" value={jobForm.charge} onChange={sj('charge')} className={inputCls} placeholder="0" /></Field>
              </div>
              <Field label="Description" required>
                <input value={jobForm.description} onChange={sj('description')} className={inputCls} placeholder="Tow — Fortitude Valley to yard" />
              </Field>
              <Field label="Vehicle">
                <input value={jobForm.vehicle} onChange={sj('vehicle')} className={inputCls} placeholder="2018 Toyota Corolla" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pickup Suburb"><input value={jobForm.pickup_suburb} onChange={sj('pickup_suburb')} className={inputCls} placeholder="Fortitude Valley" /></Field>
                <Field label="Drop-off Suburb"><input value={jobForm.dropoff_suburb} onChange={sj('dropoff_suburb')} className={inputCls} placeholder="Acacia Ridge" /></Field>
              </div>
              {jobForm.charge && (
                <div className="bg-apex-blue-50 rounded-xl p-3 flex justify-between items-center">
                  <span className="text-sm text-apex-blue-700 font-medium">Job charge</span>
                  <span className="font-black text-apex-blue-700 text-xl">${Number(jobForm.charge||0).toLocaleString()}</span>
                </div>
              )}
              <button onClick={addJob}
                disabled={!jobForm.date || !jobForm.description || !jobForm.charge || saving}
                className="w-full bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add Job to Log
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Job Confirm ── */}
      {deleteJobId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="font-black text-gray-900 mb-2">Delete This Job?</h3>
            <p className="text-sm text-gray-500 mb-5">This will remove it from the job log permanently.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteJobId(null)} className="flex-1 border-2 border-gray-200 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={async () => {
                  // We don't have a delete job endpoint yet — add it to server.py
                  // For now show a message
                  alert('Add DELETE /api/b2b/jobs/{id} to server.py to enable job deletion');
                  setDeleteJobId(null);
                }}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-xl font-bold hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Invoice Job Selection Modal ── */}
      {showInvoiceSelect && sel && (() => {
        const unpaidJobs = (sel.jobs||[]).filter(j => !j.paid);
        const selectedJobs = unpaidJobs.filter(j => selectedJobIds.includes(j.id));
        const invoiceTotal = selectedJobs.reduce((s,j) => s + j.charge, 0);
        const allSelected  = selectedJobIds.length === unpaidJobs.length;
        return (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
              {/* Header */}
              <div className="bg-apex-blue-700 rounded-t-2xl px-6 py-5 shrink-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Generate Invoice</p>
                    <p className="text-white font-black text-lg mt-0.5">{sel.business_name}</p>
                    <p className="text-white/50 text-xs mt-0.5">{unpaidJobs.length} unpaid job{unpaidJobs.length!==1?'s':''} available</p>
                  </div>
                  <button onClick={() => setShowInvoiceSelect(false)} className="bg-white/15 text-white p-2 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Select All toggle */}
              <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center shrink-0">
                <p className="text-sm font-bold text-gray-700">Select jobs to invoice</p>
                <button onClick={() => setSelectedJobIds(allSelected ? [] : unpaidJobs.map(j => j.id))}
                  className="text-xs font-bold text-apex-blue-700 hover:underline">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Job list with checkboxes */}
              <div className="overflow-y-auto flex-1 p-5 space-y-2">
                {unpaidJobs.map(j => {
                  const isSelected = selectedJobIds.includes(j.id);
                  return (
                    <button key={j.id} onClick={() => toggleJobSelect(j.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                        ${isSelected
                          ? 'border-apex-blue-500 bg-apex-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all
                        ${isSelected ? 'bg-apex-blue-700 border-apex-blue-700' : 'border-gray-300 bg-white'}`}>
                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      {/* Job info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm">{j.description}</p>
                        <div className="flex gap-3 mt-0.5 flex-wrap">
                          <span className="text-xs text-gray-400">{j.date}</span>
                          {j.vehicle && <span className="text-xs text-gray-400">{j.vehicle}</span>}
                          {j.pickup_suburb && <span className="text-xs text-gray-400">📍 {j.pickup_suburb}</span>}
                        </div>
                      </div>
                      {/* Charge */}
                      <p className={`font-black text-base shrink-0 ${isSelected ? 'text-apex-blue-700' : 'text-gray-500'}`}>
                        ${j.charge?.toLocaleString()}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Footer: total + confirm */}
              <div className="border-t border-gray-100 p-5 shrink-0 space-y-3">
                {/* Summary */}
                <div className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">
                      {selectedJobs.length} of {unpaidJobs.length} jobs selected
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      GST incl.: ${Math.round(invoiceTotal/11).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 font-bold uppercase">Invoice Total</p>
                    <p className="text-3xl font-black text-apex-blue-700">${invoiceTotal.toLocaleString()}</p>
                  </div>
                </div>
                <button onClick={generateInvoice}
                  disabled={!selectedJobIds.length || saving}
                  className="w-full py-3.5 bg-apex-yellow-500 text-apex-blue-700 font-black rounded-xl hover:bg-apex-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base">
                  {saving
                    ? <Loader className="w-5 h-5 animate-spin" />
                    : <><FileText className="w-5 h-5" /> Generate Invoice — ${invoiceTotal.toLocaleString()}</>
                  }
                </button>
                {!selectedJobIds.length && (
                  <p className="text-center text-xs text-gray-400">Select at least one job to continue</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}